import * as Ably from 'ably';
import { AblyProvider } from 'ably/react';
import { PropsWithChildren, useMemo } from 'react';
import { logging } from '../../utils/logger';

const enableAbly = process.env.REACT_APP_USE_ABLY === 'true';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

interface AblyTokenRequestResponse {
  success: boolean;
  tokenRequest: any;  // This is the actual token request object from Ably
  clientId: string;
}

export const AblyWebsocketMainProvider = (props: PropsWithChildren) => {
  const { children } = props;

  // Create Ably client with token auth for public channels
  const ablyClient = useMemo(() => {
    if (!enableAbly) {
      if (enableDevTools) console.log('Ably disabled - REACT_APP_USE_ABLY not set to true');
      // Return a mock client when disabled
      return new Ably.Realtime({
        key: "",
        clientId: "disabled",
        autoConnect: false
      });
    }

    // console.log('Creating Ably client with token auth...');

    // Request token from your server for public channel access
    const client = new Ably.Realtime({
      authCallback: async (tokenParams, callback) => {
        try {
          // console.log('🔑 Requesting Ably token for public channels...');

          const response = await fetch('/.netlify/functions/ably-public-token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              // Request access to public channels only
              channels: ['ask', 'public:*'],
              clientId: tokenParams.clientId
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to get Ably token: ${response.status}`);
          }

          const tokenData: AblyTokenRequestResponse = await response.json();

          if (tokenData.success && tokenData.tokenRequest) {
            // console.log('✅ Ably token received successfully');
            // Pass the tokenRequest object, not a .token property
            callback(null, tokenData.tokenRequest);
          } else {
            throw new Error('Invalid token response format');
          }

        } catch (error) {
          if (enableDevTools) console.error('❌ Ably token request failed:', error);
          callback(error, null);
        }
      },
      clientId: `user-${Date.now()}`, // Simple client ID
      // Connection settings
      disconnectedRetryTimeout: 5000,
      suspendedRetryTimeout: 10000,
      queryTime: true,
      // Connection stability settings to prevent idle timeout drops
      closeOnUnload: true,
      autoConnect: true,
    });

    // // Add connection event handlers for debugging
    // client.connection.on('connecting', () => {
    //   console.log('🔄 Ably connecting...');
    // });

    // client.connection.on('connected', () => {
    //   console.log('✅ Ably connected successfully:', client.connection.id);
    // });

    // client.connection.on('disconnected', () => {
    //   console.log('⚠️ Ably disconnected');
    // });

    client.connection.on('failed', (stateChange) => {
      if (stateChange.reason?.message?.includes('Timestamp not current')) {
        // Force a fresh connection
        client.close();
        setTimeout(() => {
          client.connect();
        }, 1000);
      } else {
        logging.error('Failure with Ably Client', {context: {reason: stateChange.reason}})
      }
    });

    // Handle idle timeout drops and reconnection
    client.connection.on('disconnected', (stateChange) => {
      const reason = stateChange.reason?.message || '';
      if (reason.includes('idle') || reason.includes('timeout')) {
        if (enableDevTools) console.log('🔄 Ably connection dropped due to idle timeout, will auto-reconnect');
        // Ably will automatically attempt to reconnect
      }
    });

    client.connection.on('suspended', (stateChange) => {
      const reason = stateChange.reason?.message || '';
      if (reason.includes('idle') || reason.includes('timeout')) {
        if (enableDevTools) console.log('🔄 Ably connection suspended due to idle timeout, attempting reconnection');
        // Try to force reconnection if suspended due to timeout
        setTimeout(() => {
          if (client.connection.state === 'suspended') {
            client.connect();
          }
        }, 1000);
      }
    });

    return client;
  }, []);

  return (
    <AblyProvider client={ablyClient}>
      {children}
    </AblyProvider>
  );
};
